import type { IssueType } from "@/types/technicalAudit";

export type FixMode = "auto" | "ai" | "deterministic" | "manual";

export interface IssueTypeHelp {
	title: string;
	description: string;
	impact: string;
	typicalFix: string;
	fixMode: FixMode;
}

export const FIX_MODE_LEGEND: Record<FixMode, { label: string; description: string }> = {
	auto: {
		label: "Auto",
		description: "Can run a controlled WordPress update after review, such as image alt text.",
	},
	ai: {
		label: "AI",
		description: "Needs writing or judgment first; the app records a recommendation until reviewed.",
	},
	deterministic: {
		label: "Rule",
		description: "Rule-based issue where the safest action depends on a reviewed target or setting.",
	},
	manual: {
		label: "Manual",
		description: "Needs human or infrastructure work, such as sitemap, performance, security, or blocker issues.",
	},
};

export const ISSUE_TYPE_HELP: Partial<Record<IssueType, IssueTypeHelp>> = {
	title_meta: {
		title: "Title & Meta",
		description: "The SEO title tag or meta description is missing, duplicated, too short, or too long.",
		impact: "Search results may look weak or confusing, which can reduce clicks.",
		typicalFix: "Generate or edit a clear SEO title and meta description, then apply them to the page's SEO plugin meta (Yoast/RankMath).",
		fixMode: "auto",
	},
	h1: {
		title: "H1 Heading",
		description: "The page has no clear main heading, more than one H1, or a heading mismatch.",
		impact: "Users and search engines may have a harder time understanding the page topic.",
		typicalFix:
			"Generate or edit one clear H1, then apply it to WordPress. It is written into the page body - collapsing split or duplicate H1s into a single heading and preserving page-builder layout. If the H1 can't be edited safely (e.g. it's Elementor- or theme-managed), the apply fails with a clear message so you can update it in the builder.",
		fixMode: "auto",
	},
	h1_missing: {
		title: "Missing H1",
		description: "The page has no H1 heading.",
		impact: "Without a clear main heading, users and search engines have a harder time understanding the page topic.",
		typicalFix: "Generate or edit one clear H1, then apply it to WordPress. It is written into the page body; if the H1 can't be edited safely the apply fails with a clear message.",
		fixMode: "auto",
	},
	h1_multiple: {
		title: "Multiple H1 Tags",
		description: "The page has more than one H1 heading.",
		impact: "Multiple H1s blur which heading is the page's main topic.",
		typicalFix: "Keep one canonical H1 and demote the duplicate H1(s) to H2, then apply it to WordPress — the page-builder layout is preserved. When the extra H1 is rendered by the theme/template (not the post body), it can't be edited here and is handed off for a manual theme edit.",
		fixMode: "auto",
	},
	h1_too_long: {
		title: "H1 Too Long",
		description: "The H1 heading is longer than recommended.",
		impact: "An overlong H1 dilutes the page's primary message.",
		typicalFix: "Generate or edit a concise H1, then apply it to WordPress.",
		fixMode: "auto",
	},
	duplicate_h1: {
		title: "Duplicate H1",
		description: "Multiple pages share the same visible H1 heading.",
		impact:
			"Duplicate headings make pages look interchangeable to users and search engines, weakening each page's topic signal.",
		typicalFix:
			"Generate or edit a unique on-page H1 for this page, then apply it to WordPress. It rewrites the page's visible H1 heading - not the SEO/meta title tag (Yoast/RankMath).",
		fixMode: "auto",
	},
	alt_text: {
		title: "Image Alt Text",
		description: "An image is missing descriptive alternative text.",
		impact: "This hurts accessibility and can weaken image SEO context.",
		typicalFix: "Add concise alt text to the matching WordPress media item.",
		fixMode: "auto",
	},
	redirect: {
		title: "Redirect",
		description: "A URL redirects when a direct final URL would be cleaner.",
		impact: "Redirects can slow pages and create crawl waste.",
		typicalFix: "Set the destination URL and apply a 301 redirect to it.",
		fixMode: "auto",
	},
	redirect_chain: {
		title: "Redirect Chain",
		description: "A URL passes through multiple hops before reaching its final destination.",
		impact: "Multi-hop chains waste crawl budget, slow page loads, and dilute link signals.",
		typicalFix: "Collapse the chain to a single direct 301 from the source to the final URL.",
		fixMode: "auto",
	},
	redirect_loop: {
		title: "Redirect Loop",
		description: "A URL redirects in a cycle that never resolves to a final page.",
		impact: "Crawlers and users can never reach a final page, so the URL is effectively dead.",
		typicalFix: "Review only — a loop has no safe automatic target. Open it in WordPress and repoint one hop in the loop to a live page to break the cycle.",
		fixMode: "manual",
	},
	broken_link: {
		title: "Broken Link",
		description: "A link points to a missing or failing URL.",
		impact: "Users hit dead ends and crawlers find low-quality signals.",
		typicalFix: "Replace, remove, or redirect the broken target.",
		fixMode: "deterministic",
	},
	broken_internal: {
		title: "Broken Internal URL",
		description: "An internal site URL returns an error such as 404.",
		impact: "Important pages may be unreachable from the site.",
		typicalFix: "Repoint the link to a reviewed replacement, or remove the broken link (keeping its text).",
		fixMode: "auto",
	},
	links_to_redirect: {
		title: "Links to Redirects",
		description: "This page links to URLs that redirect instead of pointing directly to the final page.",
		impact: "Redirected links are slower and waste crawl budget; direct links are cleaner.",
		typicalFix: "Generate a suggestion to follow each link, then repoint redirecting links to their final destination URL.",
		fixMode: "auto",
	},
	links_to_broken: {
		title: "Links to Broken Page",
		description: "This page links to internal URLs that return an error such as 404.",
		impact: "Visitors clicking these links hit dead ends, and crawlers waste budget on broken destinations — both hurt user experience and SEO.",
		typicalFix: "Repoint each broken link to a live replacement page, or remove it (keeping the anchor text).",
		fixMode: "auto",
	},
	broken_external: {
		title: "Broken External Link",
		description: "A link to another website is failing.",
		impact: "It creates a poor user experience and weakens trust.",
		typicalFix: "Find a replacement source or remove the link.",
		fixMode: "deterministic",
	},
	broken_image: {
		title: "Broken Image",
		description: "An image file is missing or cannot be loaded.",
		impact: "The page may look broken and lose visual context.",
		typicalFix: "Restore the image, replace it, or remove the broken reference.",
		fixMode: "deterministic",
	},
	large_image: {
		title: "Oversized Image",
		description: "An image is larger than needed for the page.",
		impact: "Large images can slow loading and hurt Core Web Vitals.",
		typicalFix: "Blog (post) images are recompressed and resized in place. Landing/service-page images are protected (review-only) since they are designed assets.",
		fixMode: "auto",
	},
	sitemap_error: {
		title: "Sitemap Issue",
		description: "A sitemap URL is broken, redirected, noindexed, or otherwise inconsistent.",
		impact: "Search engines may waste crawl budget or see conflicting index signals.",
		typicalFix: "Dead (404) sitemap URLs get a 301 redirect to the nearest live page; the URL then drops from the auto-generated sitemap. Redirect/noindex entries are resubmit-or-review only.",
		fixMode: "auto",
	},
	// Auto with per-row gating (mirrors hreflang_error): the preview endpoint
	// diagnoses WHY the page is excluded and returns a decision, and its
	// is_auto_fixable (decision === "auto_apply") is the apply authority for the
	// row. A stale sitemap cache or a page-level noindex applies cleanly; an
	// exclusion a human chose, or a whole post type excluded site-wide, needs an
	// explicit confirmation; a filter in the site's own theme/plugin code is
	// refused outright (we name the file:line, we don't edit their code).
	missing_from_sitemap: {
		title: "Missing from Sitemap",
		description:
			"A page that is live (200), indexable, and internally linked is not listed in the site's XML sitemap. This is the opposite of a Sitemap Issue (sitemap_error), which is a URL that IS in the sitemap but is broken — 404, redirected, or noindexed. It is also not an Orphan Page, which is about having zero internal links and has nothing to do with sitemaps.",
		impact:
			"Google can still find the page by following internal links, but the page is never explicitly submitted, so discovery and recrawl are slower and the page can be treated as lower priority.",
		typicalFix:
			"The fix depends on the cause, which the preview diagnoses on your server. Stale sitemap cache: Apply clears the cache and re-checks the live sitemap. Page noindex: Apply makes the page indexable and re-checks. Excluded by hand in the SEO plugin, or a whole post type excluded site-wide: Apply only runs after you explicitly confirm — a site-wide change also makes every page of that type indexable. A filter in your theme/plugin code, an unpublished or password-protected page: refused, with the exact file:line to fix at the source. Front page: not a real issue (SEO plugins list the homepage in a separate sitemap).",
		fixMode: "auto",
	},
	indexability: {
		title: "Indexability",
		description: "A page has signals that affect whether search engines can index it.",
		impact: "Important pages may not appear in search results.",
		typicalFix: "Dead (404) pages get a 301 redirect to the nearest live page. Intentional noindex/canonical pages are review-only and left unchanged.",
		fixMode: "auto",
	},
	noindex_important: {
		title: "Noindex on Important Page",
		description:
			"A page that looks important — it is in the XML sitemap, has internal links pointing to it, and sits close to the homepage — is tagged noindex, telling Google not to index it.",
		impact:
			"The page cannot appear in Google search results, so an important page earns no organic traffic while it stays noindexed.",
		typicalFix:
			'Apply sets the SEO-plugin robots meta from "noindex" to "index" (Yoast/RankMath) so Google can index the page again. Only page-level meta noindex can be fixed this way — header or robots.txt blocks need a manual fix.',
		fixMode: "auto",
	},
	canonical: {
		title: "Canonical",
		description: "The canonical tag is missing, duplicated, or points to the wrong URL.",
		impact: "Search engines may choose the wrong version of a page.",
		typicalFix: "Set one self-canonical URL on the page via the SEO plugin. Cross-domain canonicals are review-only and left unchanged unless you override.",
		fixMode: "auto",
	},
	orphan_page: {
		title: "Orphan Page",
		description: "A page exists but has no useful internal links pointing to it.",
		impact: "Users and crawlers may not discover the page naturally.",
		typicalFix: "Add an internal link from a relevant page to this orphan page. Pick source pages and edit the anchor text before applying the selected links to WordPress.",
		fixMode: "auto",
	},
	low_internal_links: {
		title: "Low Internal Links",
		description: "A page has too few internal links pointing to it.",
		impact: "The page may look less important and be harder to discover.",
		typicalFix: "Add an internal link from a relevant page to this page. Pick source pages and edit the anchor text before applying the selected links to WordPress.",
		fixMode: "auto",
	},
	deep_page: {
		title: "Deep Page",
		description: "A page takes many clicks to reach from the homepage.",
		impact: "Deep pages can be harder for users and crawlers to find.",
		typicalFix: "Improve navigation or add internal links from higher-level pages.",
		fixMode: "deterministic",
	},
	performance_diagnostics: {
		title: "Performance Diagnostics",
		description: "The crawl found speed or resource-loading concerns.",
		impact: "Slow pages can hurt user experience and conversions.",
		typicalFix: "Review render-blocking resources, compression, image size, and page weight.",
		fixMode: "manual",
	},
	slow_response: {
		title: "Slow Response",
		description: "The server took too long to respond.",
		impact: "Slow responses can reduce crawl efficiency and user satisfaction.",
		typicalFix: "Check hosting, caching, plugins, and backend bottlenecks.",
		fixMode: "manual",
	},
	multiple_meta: {
		title: "Multiple Meta Descriptions",
		description: "The page has more than one meta description tag.",
		impact: "Search engines may choose an unintended snippet.",
		typicalFix:
			"Apply the recommended single description to WordPress — it is written to BOTH the post excerpt and the SEO-plugin meta (Yoast/RankMath) so every emitted description tag matches. Two identical tags may still remain until a theme-level cleanup.",
		fixMode: "auto",
	},
	duplicate_title: {
		title: "Duplicate SEO Title Tags",
		description: "Multiple pages share the same SEO title tag.",
		impact: "Search engines and users may struggle to distinguish pages.",
		typicalFix: "Generate or edit a unique SEO title for this page, then apply it to its SEO plugin meta.",
		fixMode: "auto",
	},
	duplicate_meta: {
		title: "Duplicate Description",
		description: "Multiple pages share the same meta description.",
		impact: "Search result snippets may look repetitive or unclear.",
		typicalFix: "Generate or edit a unique meta description for this page, then apply it to its SEO plugin meta.",
		fixMode: "auto",
	},
	page_title_same_as_h1: {
		title: "Title Matches H1",
		description: "The page's SEO title tag is identical to its visible H1 heading.",
		impact: "A title that just repeats the H1 wastes a chance to add context or keywords for search results and can look thin in the SERP.",
		typicalFix: "Generate or edit an SEO title that differs from the H1, then apply it to the page's SEO plugin meta (Yoast/RankMath). The visible H1 is left unchanged.",
		fixMode: "auto",
	},
	page_title_missing: {
		title: "Missing SEO Title",
		description: "The page has no SEO title tag set.",
		impact: "Without a title tag, search engines fall back to guessing one, which usually reads poorly and reduces clicks.",
		typicalFix: "Generate or edit a clear SEO title, then apply it to the page's SEO plugin meta (Yoast/RankMath). The meta description is left unchanged.",
		fixMode: "auto",
	},
	page_title_over_60_chars: {
		title: "SEO Title Too Long",
		description: "The SEO title tag is longer than the recommended length.",
		impact: "Long titles get cut off in search results, so the end of the title is hidden from searchers.",
		typicalFix: "Generate or edit a shorter SEO title that fits, then apply it to the page's SEO plugin meta. The meta description is left unchanged.",
		fixMode: "auto",
	},
	page_title_below_30_chars: {
		title: "SEO Title Too Short",
		description: "The SEO title tag is shorter than the recommended length.",
		impact: "A thin title can under-describe the page and reduce click-through.",
		typicalFix: "Generate or edit an expanded SEO title, then apply it to the page's SEO plugin meta. The meta description is left unchanged.",
		fixMode: "auto",
	},
	meta_missing: {
		title: "Missing Meta Description",
		description: "The page has no meta description set.",
		impact: "Without a description, search engines auto-generate a snippet, which is often unclear and reduces clicks.",
		typicalFix: "Generate or edit a clear meta description, then apply it to the page's SEO plugin meta (Yoast/RankMath). The SEO title is left unchanged.",
		fixMode: "auto",
	},
	meta_description_over_155_chars: {
		title: "Meta Description Too Long",
		description: "The meta description is longer than the recommended length.",
		impact: "Long descriptions get cut off in search results, so the end of the snippet is hidden from searchers.",
		typicalFix: "Generate or edit a shorter meta description that fits, then apply it to the page's SEO plugin meta. The SEO title is left unchanged.",
		fixMode: "auto",
	},
	meta_description_below_70_chars: {
		title: "Meta Description Too Short",
		description: "The meta description is shorter than the recommended length.",
		impact: "A thin description can under-sell the page and reduce click-through.",
		typicalFix: "Generate or edit an expanded meta description, then apply it to the page's SEO plugin meta. The SEO title is left unchanged.",
		fixMode: "auto",
	},
	title_too_short: {
		title: "Title Too Short",
		description: "The SEO title tag is shorter than the recommended length.",
		impact: "A thin title can under-describe the page and reduce click-through.",
		typicalFix: "Generate or edit an expanded SEO title, then apply it to the page's SEO plugin meta.",
		fixMode: "auto",
	},
	serp_title_truncated: {
		title: "SERP Title Truncated",
		description: "The title is wide enough to be cut off in search results.",
		impact: "The end of the title may be hidden from searchers.",
		typicalFix: "Generate or edit a shorter SEO title that fits, then apply it to the page's SEO plugin meta.",
		fixMode: "auto",
	},
	serp_meta_truncated: {
		title: "SERP Description Truncated",
		description: "The meta description is long enough to be cut off in search results.",
		impact: "The end of the snippet may be hidden from searchers.",
		typicalFix: "Generate or edit a shorter meta description that fits, then apply it to the page's SEO plugin meta.",
		fixMode: "auto",
	},
	thin_content: {
		title: "Thin Content",
		description: "The page has less substantive copy than the configured review threshold.",
		impact: "Pages with too little useful content can struggle to satisfy visitors and search engines.",
		typicalFix: "Generate an expansion brief with draft sections, evidence gaps, and internal-link ideas, then add the reviewed copy manually in WordPress or the page builder.",
		fixMode: "ai",
	},
	low_readability: {
		title: "Low Readability",
		description: "The page body is hard to read: long sentences, dense paragraphs, and complex wording push the reading grade above what most visitors comfortably follow.",
		impact: "Harder-to-read pages lose visitors faster and tend to engage and convert less well, which can weaken rankings.",
		typicalFix: "Generate an AI rewrite of the page body (simpler sentences, shorter paragraphs), review and edit it, then apply. The original links, images and headings are preserved.",
		fixMode: "auto",
	},
	missing_compression: {
		title: "Missing Compression",
		description: "Responses for this page are served without GZIP or Brotli compression, so assets transfer larger than they need to.",
		impact: "Uncompressed assets increase page weight and slow load times, hurting user experience and Core Web Vitals.",
		typicalFix: "Enable GZIP or Brotli compression at the server or CDN level (Nginx/Apache config or host/CDN settings). This is a server change, not a WordPress edit, so it is surfaced as a recommendation.",
		fixMode: "manual",
	},
	missing_schema: {
		title: "Missing Schema",
		description: "A page appears to need page-appropriate structured data such as Service, FAQPage, Article, or LocalBusiness.",
		impact: "Schema can improve entity clarity and rich-result eligibility when it matches visible page content.",
		typicalFix: "Review the page intent and visible content, then add valid schema that matches the page.",
		fixMode: "manual",
	},
	schema_validation_error: {
		title: "Schema Validation Errors",
		description: "The page's structured data (schema.org JSON-LD) has validation errors that can block rich results.",
		impact: "Invalid structured data can be ignored by search engines, forfeiting rich-result eligibility.",
		typicalFix: "Review the corrected JSON-LD generated from the validation errors, then apply it — but only schema this tool injected is rewritten live; plugin/theme schema is handed off.",
		fixMode: "ai",
	},
	missing_og_tags: {
		title: "Missing Open Graph Tags",
		description: "The rendered page head is missing one or more required Open Graph tags.",
		impact: "Shared links and answer-engine previews may lack a clear title, description, image, URL, or type.",
		typicalFix: "Generate or edit the OG title, description, image, URL, and type, then apply the writable OG meta to the page's SEO plugin and verify the rendered HTML head emits the expected tags.",
		fixMode: "auto",
	},
	keyword_cannibalization: {
		title: "Keyword Cannibalization",
		description: "Multiple pages on the site target the same primary keyword, so they compete with each other in search.",
		impact: "Competing pages split ranking signals and clicks, which can hold all of them back instead of one strong page ranking well.",
		typicalFix: "Consolidate, differentiate, or canonicalize the competing pages so each primary keyword has one clear target page. This is an editorial/IA decision, so it is surfaced as a recommendation.",
		fixMode: "manual",
	},
	image_missing_dimensions: {
		title: "Image Missing Dimensions",
		description: "An image is missing explicit width and/or height attributes, so the browser cannot reserve space for it before it loads.",
		impact: "Missing dimensions cause layout shift (CLS) as the image loads, which hurts Core Web Vitals and the visitor experience.",
		typicalFix:
			"Apply adds explicit width and height attributes to the flagged <img> tag in the page content, using the image file's measured intrinsic dimensions, so the browser reserves space and prevents layout shift (CLS). Theme/builder-rendered images that are not in the post content can't be edited this way — those come back as a recommendation for a template-level fix.",
		fixMode: "auto",
	},
	hreflang_error: {
		title: "Hreflang Error",
		description:
			"A hreflang alternate tag on this page is invalid: the language/region code is malformed, the target URL is broken, or the paired page does not link back.",
		impact:
			"Broken hreflang annotations can send searchers to the wrong language or region version of a page — or make Google ignore the annotations entirely.",
		typicalFix:
			"Apply re-checks the page's hreflang tags live, then normalizes invalid language/region codes to valid ISO codes (e.g. en-UK → en-GB); broken targets are repointed to their live URL or removed. Valid tags are kept — the whole set is removed only when nothing valid remains (e.g. bogus hreflang on a single-language site). Return-link (reciprocity) errors also auto-apply when the partner page is on the same site and passes the safety checks (language verified, canonical OK, indexable) — Apply then writes matching hreflang tags to BOTH pages in one step. Cross-domain partners, same-language regional pairs, and a missing x-default alternate stay recommendation-only / manual. Apply writes the corrected tag set to WordPress after the preview confirm.",
		fixMode: "auto",
	},
	core_web_vitals: {
		title: "Core Web Vitals",
		description: "A Core Web Vitals metric (e.g. LCP, CLS, or INP) is over its recommended threshold for this page.",
		impact: "Poor Core Web Vitals slow the perceived experience and can weaken rankings, since they are a Google ranking signal.",
		typicalFix:
			"Apply enables the AgileCO CWV page optimizer for this page: missing image dimensions are injected (CLS) and below-the-fold images/iframes lazy-load with the hero image protected (LCP). Hosting/TTFB, text-only LCP, and heavy theme JavaScript still need manual performance work.",
		fixMode: "auto",
	},
};

export const getIssueTypeHelp = (issueType: IssueType | string): IssueTypeHelp => {
	return (
		ISSUE_TYPE_HELP[issueType as IssueType] || {
			title: String(issueType || "Issue"),
			description: "Definition pending for this issue type.",
			impact: "Review the issue evidence before deciding on a fix.",
			typicalFix: "Use the issue details to decide the safest manual action.",
			fixMode: "manual",
		}
	);
};

// Direct-apply issue types: the primary action is "Apply to WordPress" straight
// away — no separate review-state step and no blocking confirm modal. The
// before/after preview stays available as OPTIONAL inspection.
//   - h1 (+ atomic h1_missing / h1_multiple / h1_too_long): the generated heading
//     IS the proposed value (backend stores any legacy transition internally).
//   - multiple_meta: the LLM single-meta recommendation is auto-loaded INLINE in
//     the drawer (MultipleMetaCard) and Apply writes it to the post excerpt + SEO
//     meta in one shot, so the user never has to click into a second modal just to
//     see the suggestion.
// Other types keep their preview-confirm flow.
//   - hreflang_error is deliberately NOT direct-apply: its auto-fixability is
//     reason-dependent (invalid_lang/broken_target apply; non_reciprocal is
//     recommendation-only), and the preview endpoint's is_auto_fixable is the
//     authority — so Apply must route through the preview-confirm dialog.
export const DIRECT_APPLY_ISSUE_TYPES = new Set<string>([
	"h1",
	"h1_missing",
	"h1_multiple",
	"h1_too_long",
	"multiple_meta",
]);

export const isDirectApplyIssueType = (
	issueType: IssueType | string,
): boolean => DIRECT_APPLY_ISSUE_TYPES.has(String(issueType));

export const fixModeLabel = (mode: FixMode): string => {
	switch (mode) {
		case "auto":
			return "Auto";
		case "ai":
			return "AI";
		case "deterministic":
			return "Rule";
		default:
			return "Manual";
	}
};

export const fixModeClassName = (mode: FixMode): string => {
	switch (mode) {
		case "auto":
			return "bg-emerald-50 text-emerald-700 border-emerald-200";
		case "ai":
			return "bg-violet-50 text-violet-700 border-violet-200";
		case "deterministic":
			return "bg-sky-50 text-sky-700 border-sky-200";
		default:
			return "bg-gray-50 text-gray-700 border-gray-200";
	}
};
